import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LIBELLES_STATUT_EMPLOYE,
  anciennete,
  jourDeTunis,
  type EmployeResume,
  type StatutEmploye,
} from '@rh/shared';
import { appel } from '../api';
import { BoutonExport } from '../composants/BoutonExport';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { nomComplet } from '../format';

interface Famille {
  id: string;
  code: string;
  libelle: string;
}

const FILTRES: Array<{ cle: StatutEmploye | 'tous'; libelle: string }> = [
  { cle: 'actif', libelle: 'En poste' },
  { cle: 'sorti', libelle: 'Sortis' },
  { cle: 'tous', libelle: 'Tous' },
];

function jourLisible(jour: string): string {
  return jour.split('-').reverse().join('/');
}

/** Mêmes filtres pour la liste et pour son export. */
function parametresListe(statut: StatutEmploye | 'tous', famille: string): string {
  const parametres = new URLSearchParams();
  if (statut !== 'tous') parametres.set('statut', statut);
  if (famille) parametres.set('famille', famille);
  return parametres.toString();
}

/** Liste des employés, nés d'une candidature embauchée. */
export function Employes() {
  const [statut, setStatut] = useState<StatutEmploye | 'tous'>('actif');
  const [famille, setFamille] = useState('');
  const [familles, setFamilles] = useState<Famille[]>([]);
  const [employes, setEmployes] = useState<EmployeResume[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<Famille[]>('/familles')
      .then(setFamilles)
      .catch(() => setFamilles([]));
  }, []);

  useEffect(() => {
    setEmployes(null);
    const parametres = new URLSearchParams();
    if (statut !== 'tous') parametres.set('statut', statut);
    if (famille) parametres.set('famille', famille);
    appel<EmployeResume[]>(`/employes?${parametres.toString()}`)
      .then(setEmployes)
      .catch((e: Error) => setErreur(e.message));
  }, [statut, famille]);

  const aujourdhui = jourDeTunis();

  return (
    <section>
      <div className="entete-annonce">
        <h2>Employés</h2>
        <select value={famille} aria-label="Métier" onChange={(e) => setFamille(e.target.value)}>
          <option value="">Tous les métiers</option>
          {familles.map((f) => (
            <option key={f.id} value={f.code}>
              {f.libelle}
            </option>
          ))}
        </select>
      </div>
      {employes && employes.length > 0 && (
        <p className="actions-liste">
          <BoutonExport chemin={`/exports/employes?${parametresListe(statut, famille)}`} nomParDefaut="rh-employes.xlsx" />
        </p>
      )}

      <div className="filtres" role="group" aria-label="Statut">
        {FILTRES.map((f) => (
          <button key={f.cle} type="button" className={f.cle === statut ? 'actif' : ''} onClick={() => setStatut(f.cle)}>
            {f.libelle}
          </button>
        ))}
      </div>

      {erreur && <Erreur message={erreur} />}
      {!employes && !erreur && <Chargement quoi="des employés" />}
      {employes?.length === 0 && (
        <Vide enfants="Aucun employé. Une fiche se crée depuis une candidature, une fois les justificatifs reçus." />
      )}

      <ul className="candidats">
        {(employes ?? []).map((employe) => (
          <li key={employe.id} className="carte-candidat">
            <Link to={`/employes/${employe.id}`} className="carte-lien">
              <div className="corps">
                <div className="ligne1">
                  <strong>{nomComplet(employe)}</strong>
                  {employe.matricule && <code>{employe.matricule}</code>}
                  <span className={`statut-annonce statut-employe-${employe.statut}`}>
                    {LIBELLES_STATUT_EMPLOYE[employe.statut]}
                  </span>
                </div>
                <p className="synthese">
                  {[employe.poste, employe.pointDeVente].filter(Boolean).join(' · ')}
                </p>
                <div className="meta">
                  {employe.famille && <span>{employe.famille}</span>}
                  <span>Entrée le {jourLisible(employe.dateEntree)}</span>
                  {employe.dateSortie ? (
                    <span>Sortie le {jourLisible(employe.dateSortie)}</span>
                  ) : (
                    <span>{anciennete(employe.dateEntree, aujourdhui)}</span>
                  )}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
